// Terms of Service content, externalized for i18n (same pattern as privacyContent.js).
// English is the source of truth; uk[]/ru[] are filled in a later step.
// Rendered by components/LegalDoc.js. Node vocabulary:
//   { type: "h2" | "h3" | "p", text }, { type: "p", html }, { type: "ul", items: [...] }
//   optional { type: "p", ..., className } for the few specially-styled paragraphs.

export const TERMS_META = {
  title: { en: "Terms of Service", uk: "Умови використання", ru: "Условия использования" },
  effectiveDate: { en: "May 11, 2026", uk: "11 травня 2026 р.", ru: "11 мая 2026 г." },
  lastUpdated: { en: "August 19, 2026", uk: "19 серпня 2026 р.", ru: "19 августа 2026 г." },
  labels: {
    effective: { en: "Effective Date", uk: "Чинні з", ru: "Действуют с" },
    updated: { en: "Last Updated", uk: "Оновлено", ru: "Обновлено" },
    backShort: { en: "MealPill", uk: "MealPill", ru: "MealPill" },
    backLong: { en: "Back to MealPill", uk: "Назад до MealPill", ru: "Назад в MealPill" },
    contact: { en: "Contact", uk: "Контакти", ru: "Контакты" },
    // Cross-link to the sibling legal page.
    cross: { to: "/privacy", label: { en: "Privacy Policy", uk: "Політика конфіденційності", ru: "Политика конфиденциальности" } },
  },
};

export const TERMS = {
  en: [
    // 1. Acceptance of Terms
    { type: "h2", text: "1. Acceptance of Terms" },
    { type: "p", text: "By accessing or using MealPill (the \"Service\") at mealpill.app, operated by Dmytro Kravchuk (\"MealPill,\" \"we,\" \"us,\" \"our\"), you agree to be bound by these Terms of Service (\"Terms\"). If you do not agree, do not use the Service." },
    { type: "p", text: "These Terms form a legally binding agreement between you and MealPill." },

    // 2. Eligibility
    { type: "h2", text: "2. Eligibility" },
    { type: "p", text: "To use the Service, you must:" },
    { type: "ul", items: [
      { text: "Be at least 13 years of age (or the minimum age required by law in your jurisdiction)." },
      { text: "Have the legal capacity to enter into a binding agreement." },
      { text: "Not be barred from using the Service under applicable laws." },
      { text: "Provide accurate registration information and keep it updated." },
    ] },

    // 3. Account Registration
    { type: "h2", text: "3. Account Registration" },
    { type: "p", text: "You are responsible for:" },
    { type: "ul", items: [
      { text: "Maintaining the confidentiality of your password and login credentials." },
      { text: "All activities under your account." },
      { text: "Notifying us immediately of unauthorized access (email hello@mealpill.app)." },
    ] },
    { type: "p", text: "We may suspend or terminate accounts that violate these Terms." },

    // 4. Service Description
    { type: "h2", text: "4. Service Description" },
    { type: "p", text: "MealPill provides:" },
    { type: "ul", items: [
      { text: "AI-generated meal planning based on user preferences and inventory." },
      { text: "Virtual fridge tracking with expiry management." },
      { text: "Shopping list generation." },
      { text: "Recipe suggestions and storage." },
      { text: "Weekly nutritional insights." },
      { text: "Family-shared planning (planned feature)." },
    ] },
    { type: "p", text: "Features may change, be removed, or be temporarily unavailable." },

    // 5. Subscription and Payment Terms
    { type: "h2", text: "5. Subscription and Payment Terms" },
    { type: "h3", text: "5.1 Subscription Plans" },
    { type: "p", text: "MealPill offers paid subscriptions. Current plans include:" },
    { type: "ul", items: [
      { text: "Individual subscription (available now)." },
      { text: "Family subscription (coming soon)." },
    ] },
    { type: "p", text: "Pricing and billing frequency (monthly or annual) are shown before payment. Subscriptions renew automatically at the end of each billing period unless canceled." },
    { type: "h3", text: "5.2 Free Trial" },
    { type: "p", text: "New subscriptions include a 7-day free trial. A valid payment method is required to start the trial. Unless you cancel before the trial ends, your payment method will be charged automatically for the selected plan on the date the trial expires, at the price shown at checkout. You may cancel at any time during the trial through the subscription management portal at no charge." },
    { type: "h3", text: "5.3 Payments" },
    { type: "p", text: "Payments are processed by Stripe, Inc. By providing payment information, you authorize MealPill to charge your selected payment method." },
    { type: "h3", text: "5.4 Refunds" },
    { type: "p", text: "We offer a 14-day satisfaction guarantee on initial paid subscriptions. After 14 days, payments are non-refundable except as required by law. Cancel anytime to stop future charges. This paragraph does not limit any refund you are entitled to under Section 5.5 or under applicable consumer protection legislation." },
    { type: "h3", text: "5.5 Automatic Renewal and Cancellation" },
    { type: "h3", text: "5.5.1 Monthly Plans" },
    { type: "p", text: "Your monthly subscription renews automatically at the end of each billing period. You may cancel the renewal at any time, whether before or after the renewal date, through the subscription management portal, without charge or penalty." },
    { type: "h3", text: "5.5.2 Annual Plans" },
    { type: "p", text: "Your annual subscription renews automatically at the end of each 12-month term. Not less than 30 days and not more than 60 days before each renewal date, we will send a notice to your account email address stating the renewal date, the length of the renewal term, the amount to be charged, and how to cancel. If you cancel before the renewal date, no charge or penalty applies. If you cancel after the renewal date, you are entitled to a refund of the portion of the renewal fee corresponding to the unused part of the renewal term; we will issue that refund within 15 days of receiving your cancellation." },

    // 6. AI-Generated Content Disclaimer
    { type: "h2", text: "6. AI-Generated Content Disclaimer" },
    { type: "p", text: "This section is critical. Read carefully.", className: "font-medium uppercase tracking-wide text-[#9A6B5C] text-sm mb-3" },
    { type: "p", text: "MealPill uses AI to generate meal plans, recipes, nutritional information, ingredient substitutions, and dietary suggestions. AI output:" },
    { type: "ul", items: [
      { html: "<strong>Is not medical, nutritional, or dietary advice.</strong> Consult a licensed dietitian, nutritionist, or physician for personalized guidance." },
      { html: "<strong>May contain errors, inaccuracies, or outdated information.</strong> Verify allergen information and dietary restrictions before consumption." },
      { html: "<strong>May not accurately reflect your specific health conditions, allergies, or dietary requirements.</strong>" },
    ] },
    { type: "h3", text: "6.1 Allergen and Dietary Restriction Warning" },
    { type: "p", text: "If you have food allergies, dietary restrictions, or specific health conditions:" },
    { type: "ul", items: [
      { text: "You are responsible for verifying every ingredient and recipe before consumption." },
      { text: "Do not rely solely on MealPill's filters or suggestions." },
      { text: "Cross-check with ingredient labels and trusted medical sources." },
      { text: "MealPill is not liable for adverse reactions, illness, or harm caused by reliance on AI-generated content." },
    ] },
    { type: "h3", text: "6.2 Recipe Accuracy" },
    { type: "p", text: "Recipes may contain measurement errors, missing steps, or incorrect cooking times. Use your culinary judgment." },

    // 7. User Content
    { type: "h2", text: "7. User Content" },
    { type: "h3", text: "7.1 Your Content" },
    { type: "p", text: "You may create or upload content (custom recipes, notes, photos). You retain ownership but grant MealPill a worldwide, non-exclusive, royalty-free license to:" },
    { type: "ul", items: [
      { text: "Store, display, and process your content to provide the Service." },
      { text: "Improve product features (using aggregated, anonymized data)." },
    ] },
    { type: "h3", text: "7.2 Content Rules" },
    { type: "p", text: "You may not upload content that:" },
    { type: "ul", items: [
      { text: "Infringes intellectual property rights of others." },
      { text: "Is illegal, harmful, harassing, or defamatory." },
      { text: "Contains malware or exploits." },
      { text: "Promotes harmful eating behaviors or unsafe dietary practices." },
      { text: "Violates any applicable law." },
    ] },
    { type: "p", text: "We may remove content violating these rules without notice." },

    // 8. Acceptable Use
    { type: "h2", text: "8. Acceptable Use" },
    { type: "p", text: "You agree not to:" },
    { type: "ul", items: [
      { text: "Reverse engineer, decompile, or attempt to extract source code." },
      { text: "Use the Service to build a competing product." },
      { text: "Scrape or harvest data from the Service." },
      { text: "Attempt to bypass rate limits, security measures, or paywalls." },
      { text: "Impersonate others." },
      { text: "Use automated systems (bots) to access the Service without permission." },
      { text: "Resell, sublicense, or commercially exploit the Service." },
    ] },

    // 9. Intellectual Property
    { type: "h2", text: "9. Intellectual Property" },
    { type: "p", text: "The MealPill name, logo, software, design, and original content are owned by Dmytro Kravchuk and protected by copyright, trademark, and other laws. Trademark filing for MealPill is in progress." },
    { type: "p", text: "You may not use our trademarks without prior written permission." },

    // 10. Third-Party Services
    { type: "h2", text: "10. Third-Party Services" },
    { type: "p", text: "The Service integrates with:" },
    { type: "ul", items: [
      { text: "Google Gemini (AI processing)." },
      { text: "Stripe (payments)." },
      { text: "Railway, Vercel (hosting)." },
    ] },
    { type: "p", text: "Your use of third-party services is subject to their respective terms and privacy policies." },

    // 11. Disclaimers
    { type: "h2", text: "11. Disclaimers" },
    { type: "p", text: "The Service is provided \"as is\" and \"as available\" without warranties of any kind, either express or implied, including without limitation:", className: "uppercase tracking-wide text-sm font-medium" },
    { type: "ul", items: [
      { text: "Warranties of merchantability, fitness for a particular purpose, non-infringement." },
      { text: "Warranties of accuracy, reliability, completeness of content." },
      { text: "Warranties that the Service will be uninterrupted, secure, or error-free." },
      { text: "Warranties of nutritional accuracy or suitability for any health condition." },
    ] },
    { type: "p", text: "We do not guarantee specific results from using the Service (e.g., weight loss, food savings, time saved)." },

    // 12. Limitation of Liability
    { type: "h2", text: "12. Limitation of Liability" },
    { type: "p", text: "To the maximum extent permitted by law:", className: "uppercase tracking-wide text-sm font-medium" },
    { type: "ul", items: [
      { text: "MealPill (Dmytro Kravchuk) shall not be liable for indirect, incidental, special, consequential, or punitive damages." },
      { text: "Total cumulative liability for any claim shall not exceed the amount you paid to MealPill in the 12 months prior to the claim, or CAD $100, whichever is greater." },
      { text: "MealPill is not liable for damages arising from: AI-generated content, allergic reactions, food poisoning, dietary harm, third-party services, force majeure events." },
    ] },
    { type: "p", text: "Some jurisdictions do not allow exclusion of certain warranties or limitation of liability — these provisions apply to the maximum extent permitted by law." },

    // 13. Indemnification
    { type: "h2", text: "13. Indemnification" },
    { type: "p", text: "You agree to indemnify and hold harmless Dmytro Kravchuk and MealPill from any claims, damages, or expenses arising from:" },
    { type: "ul", items: [
      { text: "Your use of the Service." },
      { text: "Your User Content." },
      { text: "Your violation of these Terms." },
      { text: "Your violation of any rights of third parties." },
    ] },

    // 14. Termination
    { type: "h2", text: "14. Termination" },
    { type: "h3", text: "14.1 By You" },
    { type: "p", text: "You may terminate your account at any time by emailing hello@mealpill.app or using account deletion features in settings." },
    { type: "h3", text: "14.2 By Us" },
    { type: "p", text: "We may suspend or terminate your account if you:" },
    { type: "ul", items: [
      { text: "Violate these Terms." },
      { text: "Engage in fraudulent or harmful activity." },
      { text: "Fail to pay subscription fees." },
      { text: "Are inactive for 24+ months." },
    ] },
    { type: "p", text: "Upon termination, your access ends but Sections 6, 8, 9, 11, 12, 13, 15, and 16 survive." },

    // 15. Governing Law and Disputes
    { type: "h2", text: "15. Governing Law and Disputes" },
    { type: "p", text: "These Terms are governed by the laws of British Columbia, Canada, without regard to conflict of law principles." },
    { type: "h3", text: "15.1 Informal Resolution" },
    { type: "p", text: "For any dispute, contact us at hello@mealpill.app first. We will attempt to resolve disputes informally within 30 days." },
    { type: "h3", text: "15.2 Jurisdiction" },
    { type: "p", text: "If informal resolution fails, disputes shall be brought in the courts of British Columbia, Canada. You waive any objection to jurisdiction or venue." },

    // 16. Changes to Terms
    { type: "h2", text: "16. Changes to Terms" },
    { type: "h3", text: "16.1 Amendable Provisions" },
    { type: "p", text: "We may unilaterally amend the following provisions of these Terms: Section 4 (Service Description), Section 8 (Acceptable Use), Section 10 (Third-Party Services), and subscription pricing under Section 5.1. No other provision may be amended without your agreement." },
    { type: "h3", text: "16.2 Limits on Amendments" },
    { type: "p", text: "We will not amend any provision respecting cancellation, returns, exchanges or refunds in a way that increases an obligation of yours or reduces an obligation of ours." },
    { type: "h3", text: "16.3 Notice of Amendment" },
    { type: "p", text: "We will notify you of any amendment under Section 16.1 not less than 30 days and not more than 60 days before it takes effect, by email to your account address. The notice will set out the existing provision, the amended provision, the date the amendment takes effect, and your right to cancel your subscription without charge or penalty if you do not accept the amendment." },

    // 17. General
    { type: "h2", text: "17. General" },
    { type: "ul", items: [
      { html: "<strong>Entire Agreement:</strong> These Terms (with Privacy Policy) constitute the entire agreement." },
      { html: "<strong>Severability:</strong> If any provision is unenforceable, the rest remains in effect." },
      { html: "<strong>No Waiver:</strong> Our failure to enforce a provision is not a waiver." },
      { html: "<strong>Assignment:</strong> You may not assign these Terms; we may assign them in connection with a business transfer." },
      { html: "<strong>Governing Language:</strong> These Terms are drafted in English. Translations are provided for convenience only. In case of any discrepancy between the English version and a translation, the English version prevails." },
    ] },

    // 18. Contact
    { type: "h2", text: "18. Contact" },
    { type: "p", text: "Questions about these Terms:" },
    { type: "p", html: "<strong>Email:</strong> hello@mealpill.app<br /><strong>Address:</strong> Dmytro Kravchuk, 525-503 Ninth Street, New Westminster, BC V3M 5T9, Canada" },
  ],
  uk: [
    // 1. Acceptance of Terms
    { type: "h2", text: "1. Прийняття Умов" },
    { type: "p", text: "Отримуючи доступ до MealPill («Сервіс») за адресою mealpill.app, яким керує Дмитро Кравчук («MealPill», «ми», «нас», «наш»), або користуючись ним, ви погоджуєтесь дотримуватися цих Умов використання («Умови»). Якщо ви не згодні з ними, не користуйтеся Сервісом." },
    { type: "p", text: "Ці Умови є юридично зобов'язальною угодою між вами та MealPill." },

    // 2. Eligibility
    { type: "h2", text: "2. Право на використання" },
    { type: "p", text: "Щоб користуватися Сервісом, ви повинні:" },
    { type: "ul", items: [
      { text: "Мати щонайменше 13 років (або досягти мінімального віку, встановленого законом у вашій юрисдикції)." },
      { text: "Мати дієздатність, достатню для укладення зобов'язальної угоди." },
      { text: "Не мати заборони на користування Сервісом згідно з чинним законодавством." },
      { text: "Надавати достовірні реєстраційні дані та підтримувати їх в актуальному стані." },
    ] },

    // 3. Account Registration
    { type: "h2", text: "3. Реєстрація акаунта" },
    { type: "p", text: "Ви відповідаєте за:" },
    { type: "ul", items: [
      { text: "Збереження конфіденційності вашого пароля та даних для входу." },
      { text: "Усі дії, вчинені під вашим акаунтом." },
      { text: "Негайне повідомлення нас про несанкціонований доступ (email hello@mealpill.app)." },
    ] },
    { type: "p", text: "Ми можемо призупинити або видалити акаунти, які порушують ці Умови." },

    // 4. Service Description
    { type: "h2", text: "4. Опис Сервісу" },
    { type: "p", text: "MealPill надає:" },
    { type: "ul", items: [
      { text: "Планування харчування, згенероване ШІ на основі ваших уподобань і наявних продуктів." },
      { text: "Віртуальний облік холодильника з відстеженням термінів придатності." },
      { text: "Формування списку покупок." },
      { text: "Пропозиції рецептів та їх збереження." },
      { text: "Щотижневі висновки щодо харчування." },
      { text: "Спільне сімейне планування (запланована функція)." },
    ] },
    { type: "p", text: "Функції можуть змінюватися, вилучатися або бути тимчасово недоступними." },

    // 5. Subscription and Payment Terms
    { type: "h2", text: "5. Умови підписки та оплати" },
    { type: "h3", text: "5.1 Тарифи підписки" },
    { type: "p", text: "MealPill пропонує платні підписки. Поточні тарифи включають:" },
    { type: "ul", items: [
      { text: "Індивідуальну підписку (доступна зараз)." },
      { text: "Сімейну підписку (незабаром)." },
    ] },
    { type: "p", text: "Вартість і період оплати (місячний або річний) відображаються до оплати. Підписки поновлюються автоматично наприкінці кожного розрахункового періоду, якщо їх не скасовано." },
    { type: "h3", text: "5.2 Безкоштовний пробний період" },
    { type: "p", text: "Нові підписки включають 7-денний безкоштовний пробний період. Для початку пробного періоду потрібен дійсний спосіб оплати. Якщо ви не скасуєте підписку до завершення пробного періоду, з вашого способу оплати буде автоматично списано вартість обраного плану в день завершення пробного періоду за ціною, показаною під час оформлення. Ви можете скасувати підписку будь-коли протягом пробного періоду через портал керування підпискою без стягнення плати." },
    { type: "h3", text: "5.3 Платежі" },
    { type: "p", text: "Платежі обробляє Stripe, Inc. Надаючи платіжні дані, ви уповноважуєте MealPill списувати кошти з обраного вами способу оплати." },
    { type: "h3", text: "5.4 Повернення коштів" },
    { type: "p", text: "На першу платну підписку ми надаємо 14-денну гарантію задоволення. Після 14 днів кошти не повертаються, окрім випадків, передбачених законом. Ви можете скасувати підписку будь-коли, щоб припинити подальші списання. Цей пункт не обмежує жодного відшкодування, на яке ви маєте право згідно з Розділом 5.5 або згідно з чинним законодавством про захист прав споживачів." },
    { type: "h3", text: "5.5 Автоматичне поновлення та скасування" },
    { type: "h3", text: "5.5.1 Місячні плани" },
    { type: "p", text: "Ваша місячна підписка поновлюється автоматично наприкінці кожного платіжного періоду. Ви можете скасувати поновлення будь-коли — як до, так і після дати поновлення — через портал керування підпискою, без стягнення плати чи штрафу." },
    { type: "h3", text: "5.5.2 Річні плани" },
    { type: "p", text: "Ваша річна підписка поновлюється автоматично наприкінці кожного 12-місячного строку. Не менше ніж за 30 днів і не більше ніж за 60 днів до кожної дати поновлення ми надішлемо повідомлення на адресу електронної пошти вашого акаунта із зазначенням дати поновлення, тривалості нового строку, суми до списання та способу скасування. Якщо ви скасуєте підписку до дати поновлення, плата чи штраф не стягуються. Якщо ви скасуєте підписку після дати поновлення, ви маєте право на відшкодування частини плати за поновлення, що відповідає невикористаній частині нового строку; ми здійснимо таке відшкодування протягом 15 днів з моменту отримання вашого повідомлення про скасування." },

    // 6. AI-Generated Content Disclaimer
    { type: "h2", text: "6. Застереження щодо контенту, згенерованого ШІ" },
    { type: "p", text: "Цей розділ є критично важливим. Прочитайте уважно.", className: "font-medium uppercase tracking-wide text-[#9A6B5C] text-sm mb-3" },
    { type: "p", text: "MealPill використовує ШІ для створення планів харчування, рецептів, даних про поживність, замін інгредієнтів та дієтичних порад. Результат роботи ШІ:" },
    { type: "ul", items: [
      { html: "<strong>Не є медичною, нутриціологічною чи дієтичною порадою.</strong> Для персональних рекомендацій зверніться до ліцензованого дієтолога, нутриціолога або лікаря." },
      { html: "<strong>Може містити помилки, неточності або застарілу інформацію.</strong> Перевіряйте дані про алергени та дієтичні обмеження перед вживанням." },
      { html: "<strong>Може неточно враховувати ваш стан здоров'я, алергії чи дієтичні вимоги.</strong>" },
    ] },
    { type: "h3", text: "6.1 Попередження про алергени та дієтичні обмеження" },
    { type: "p", text: "Якщо у вас є харчова алергія, дієтичні обмеження або особливі стани здоров'я:" },
    { type: "ul", items: [
      { text: "Ви самі відповідаєте за перевірку кожного інгредієнта та рецепта перед вживанням." },
      { text: "Не покладайтеся виключно на фільтри чи пропозиції MealPill." },
      { text: "Звіряйте інформацію з етикетками продуктів і надійними медичними джерелами." },
      { text: "MealPill не несе відповідальності за побічні реакції, захворювання чи шкоду, спричинені покладанням на контент, згенерований ШІ." },
    ] },
    { type: "h3", text: "6.2 Точність рецептів" },
    { type: "p", text: "Рецепти можуть містити помилки у мірах, пропущені кроки або неправильний час приготування. Покладайтеся на власний кулінарний досвід." },

    // 7. User Content
    { type: "h2", text: "7. Контент користувача" },
    { type: "h3", text: "7.1 Ваш контент" },
    { type: "p", text: "Ви можете створювати або завантажувати контент (власні рецепти, нотатки, фотографії). Ви зберігаєте право власності на нього, але надаєте MealPill всесвітню, невиключну, безоплатну ліцензію на:" },
    { type: "ul", items: [
      { text: "Зберігання, відображення та обробку вашого контенту для надання Сервісу." },
      { text: "Вдосконалення функцій продукту (з використанням агрегованих знеособлених даних)." },
    ] },
    { type: "h3", text: "7.2 Правила щодо контенту" },
    { type: "p", text: "Ви не маєте права завантажувати контент, який:" },
    { type: "ul", items: [
      { text: "Порушує права інтелектуальної власності інших осіб." },
      { text: "Є незаконним, шкідливим, образливим або наклепницьким." },
      { text: "Містить шкідливе програмне забезпечення чи експлойти." },
      { text: "Пропагує шкідливу харчову поведінку або небезпечні дієтичні практики." },
      { text: "Порушує будь-яке чинне законодавство." },
    ] },
    { type: "p", text: "Ми можемо видалити контент, що порушує ці правила, без попередження." },

    // 8. Acceptable Use
    { type: "h2", text: "8. Допустиме використання" },
    { type: "p", text: "Ви зобов'язуєтесь не:" },
    { type: "ul", items: [
      { text: "Здійснювати зворотну розробку, декомпіляцію чи спроби витягти вихідний код." },
      { text: "Використовувати Сервіс для створення конкурентного продукту." },
      { text: "Здійснювати скрейпінг або масовий збір даних із Сервісу." },
      { text: "Намагатися обійти обмеження частоти запитів, засоби безпеки чи платний доступ." },
      { text: "Видавати себе за інших осіб." },
      { text: "Використовувати автоматизовані системи (боти) для доступу до Сервісу без дозволу." },
      { text: "Перепродавати, субліцензувати чи комерційно експлуатувати Сервіс." },
    ] },

    // 9. Intellectual Property
    { type: "h2", text: "9. Інтелектуальна власність" },
    { type: "p", text: "Назва MealPill, логотип, програмне забезпечення, дизайн та оригінальний контент належать Дмитру Кравчуку й захищені законодавством про авторське право, торговельні марки та іншими нормами. Заявку на реєстрацію торговельної марки MealPill подано й перебуває на розгляді." },
    { type: "p", text: "Ви не маєте права використовувати наші торговельні марки без попереднього письмового дозволу." },

    // 10. Third-Party Services
    { type: "h2", text: "10. Сервіси третіх сторін" },
    { type: "p", text: "Сервіс інтегрується з:" },
    { type: "ul", items: [
      { text: "Google Gemini (обробка ШІ)." },
      { text: "Stripe (платежі)." },
      { text: "Railway, Vercel (хостинг)." },
    ] },
    { type: "p", text: "Використання вами сервісів третіх сторін регулюється їхніми власними умовами та політиками конфіденційності." },

    // 11. Disclaimers
    { type: "h2", text: "11. Застереження щодо гарантій" },
    { type: "p", text: "Сервіс надається «як є» та «як доступно», без будь-яких гарантій, прямих чи непрямих, включно з, зокрема:", className: "uppercase tracking-wide text-sm font-medium" },
    { type: "ul", items: [
      { text: "Гарантіями придатності до продажу, придатності для певної мети, відсутності порушення прав третіх осіб." },
      { text: "Гарантіями точності, надійності чи повноти контенту." },
      { text: "Гарантіями безперебійної, захищеної чи безпомилкової роботи Сервісу." },
      { text: "Гарантіями точності даних про поживність або придатності для будь-якого стану здоров'я." },
    ] },
    { type: "p", text: "Ми не гарантуємо конкретних результатів від використання Сервісу (наприклад, зниження ваги, економії на продуктах, заощадження часу)." },

    // 12. Limitation of Liability
    { type: "h2", text: "12. Обмеження відповідальності" },
    { type: "p", text: "У максимальному обсязі, дозволеному законом:", className: "uppercase tracking-wide text-sm font-medium" },
    { type: "ul", items: [
      { text: "MealPill (Дмитро Кравчук) не несе відповідальності за непрямі, випадкові, спеціальні, побічні або штрафні збитки." },
      { text: "Сукупна відповідальність за будь-якою вимогою не перевищує суми, сплаченої вами MealPill протягом 12 місяців до подання вимоги, або 100 канадських доларів — залежно від того, що більше." },
      { text: "MealPill не несе відповідальності за збитки, спричинені: контентом, згенерованим ШІ; алергічними реакціями; харчовим отруєнням; шкодою від дієти; сервісами третіх сторін; обставинами непереборної сили." },
    ] },
    { type: "p", text: "Деякі юрисдикції не допускають виключення певних гарантій чи обмеження відповідальності — ці положення застосовуються в максимальному обсязі, дозволеному законом." },

    // 13. Indemnification
    { type: "h2", text: "13. Відшкодування збитків" },
    { type: "p", text: "Ви зобов'язуєтесь відшкодувати та убезпечити Дмитра Кравчука і MealPill від будь-яких претензій, збитків чи витрат, що виникають унаслідок:" },
    { type: "ul", items: [
      { text: "Вашого використання Сервісу." },
      { text: "Вашого контенту користувача." },
      { text: "Порушення вами цих Умов." },
      { text: "Порушення вами будь-яких прав третіх осіб." },
    ] },

    // 14. Termination
    { type: "h2", text: "14. Припинення" },
    { type: "h3", text: "14.1 З вашої ініціативи" },
    { type: "p", text: "Ви можете припинити дію свого акаунта будь-коли, написавши на hello@mealpill.app або скориставшись функціями видалення акаунта в налаштуваннях." },
    { type: "h3", text: "14.2 З нашої ініціативи" },
    { type: "p", text: "Ми можемо призупинити або припинити дію вашого акаунта, якщо ви:" },
    { type: "ul", items: [
      { text: "Порушуєте ці Умови." },
      { text: "Вчиняєте шахрайські або шкідливі дії." },
      { text: "Не сплачуєте вартість підписки." },
      { text: "Неактивні понад 24 місяці." },
    ] },
    { type: "p", text: "Після припинення ваш доступ завершується, але розділи 6, 8, 9, 11, 12, 13, 15 і 16 продовжують діяти." },

    // 15. Governing Law and Disputes
    { type: "h2", text: "15. Застосовне право та спори" },
    { type: "p", text: "Ці Умови регулюються законодавством провінції Британська Колумбія, Канада, без урахування колізійних норм." },
    { type: "h3", text: "15.1 Неформальне врегулювання" },
    { type: "p", text: "У разі будь-якого спору спершу зверніться до нас на hello@mealpill.app. Ми намагатимемося врегулювати спір неформально протягом 30 днів." },
    { type: "h3", text: "15.2 Підсудність" },
    { type: "p", text: "Якщо неформальне врегулювання не вдасться, спори розглядатимуться судами провінції Британська Колумбія, Канада. Ви відмовляєтесь від будь-яких заперечень щодо підсудності чи місця розгляду." },

    // 16. Changes to Terms
    { type: "h2", text: "16. Зміни до Умов" },
    { type: "h3", text: "16.1 Положення, що можуть бути змінені" },
    { type: "p", text: "Ми можемо в односторонньому порядку змінювати такі положення цих Умов: Розділ 4 (Опис Сервісу), Розділ 8 (Прийнятне використання), Розділ 10 (Сторонні сервіси) та вартість підписки згідно з Розділом 5.1. Жодне інше положення не може бути змінене без вашої згоди." },
    { type: "h3", text: "16.2 Обмеження щодо змін" },
    { type: "p", text: "Ми не змінюватимемо положень щодо скасування, повернення, обміну чи відшкодування у спосіб, який збільшує ваші обов'язки або зменшує наші." },
    { type: "h3", text: "16.3 Повідомлення про зміни" },
    { type: "p", text: "Ми повідомимо вас про будь-яку зміну згідно з Розділом 16.1 не менше ніж за 30 днів і не більше ніж за 60 днів до набрання нею чинності, електронним листом на адресу вашого акаунта. У повідомленні буде зазначено чинне положення, змінене положення, дату набрання чинності зміною, а також ваше право скасувати підписку без стягнення плати чи штрафу, якщо ви не приймаєте зміну." },

    // 17. General
    { type: "h2", text: "17. Загальні положення" },
    { type: "ul", items: [
      { html: "<strong>Повнота угоди:</strong> Ці Умови (разом із Політикою конфіденційності) становлять повну угоду між сторонами." },
      { html: "<strong>Подільність:</strong> Якщо будь-яке положення виявиться нездійсненним, решта залишається чинною." },
      { html: "<strong>Відсутність відмови від прав:</strong> Невикористання нами будь-якого положення не означає відмови від нього." },
      { html: "<strong>Передача прав:</strong> Ви не можете передавати ці Умови; ми можемо передати їх у зв'язку з переходом бізнесу." },
      { html: "<strong>Мова документа:</strong> Ці Умови складено англійською мовою. Переклади надано виключно для зручності. У разі будь-яких розбіжностей між англійською версією та перекладом переважає англійська версія." },
    ] },

    // 18. Contact
    { type: "h2", text: "18. Контакти" },
    { type: "p", text: "Питання щодо цих Умов:" },
    { type: "p", html: "<strong>Email:</strong> hello@mealpill.app<br /><strong>Адреса:</strong> Дмитро Кравчук, 525-503 Ninth Street, New Westminster, BC V3M 5T9, Canada" },
  ],
  ru: [
    // 1. Acceptance of Terms
    { type: "h2", text: "1. Принятие Условий" },
    { type: "p", text: "Получая доступ к MealPill («Сервис») по адресу mealpill.app, которым управляет Дмитрий Кравчук («MealPill», «мы», «нас», «наш»), или пользуясь им, вы соглашаетесь соблюдать настоящие Условия использования («Условия»). Если вы с ними не согласны, не пользуйтесь Сервисом." },
    { type: "p", text: "Настоящие Условия являются юридически обязывающим соглашением между вами и MealPill." },

    // 2. Eligibility
    { type: "h2", text: "2. Право на использование" },
    { type: "p", text: "Чтобы пользоваться Сервисом, вы должны:" },
    { type: "ul", items: [
      { text: "Быть не младше 13 лет (или достичь минимального возраста, установленного законом в вашей юрисдикции)." },
      { text: "Обладать дееспособностью, достаточной для заключения обязывающего соглашения." },
      { text: "Не иметь запрета на пользование Сервисом согласно применимому законодательству." },
      { text: "Предоставлять достоверные регистрационные данные и поддерживать их в актуальном состоянии." },
    ] },

    // 3. Account Registration
    { type: "h2", text: "3. Регистрация аккаунта" },
    { type: "p", text: "Вы отвечаете за:" },
    { type: "ul", items: [
      { text: "Сохранение конфиденциальности вашего пароля и данных для входа." },
      { text: "Все действия, совершённые под вашим аккаунтом." },
      { text: "Незамедлительное уведомление нас о несанкционированном доступе (email hello@mealpill.app)." },
    ] },
    { type: "p", text: "Мы можем приостановить или удалить аккаунты, нарушающие настоящие Условия." },

    // 4. Service Description
    { type: "h2", text: "4. Описание Сервиса" },
    { type: "p", text: "MealPill предоставляет:" },
    { type: "ul", items: [
      { text: "Планирование питания, сгенерированное ИИ на основе ваших предпочтений и имеющихся продуктов." },
      { text: "Виртуальный учёт холодильника с отслеживанием сроков годности." },
      { text: "Формирование списка покупок." },
      { text: "Предложения рецептов и их сохранение." },
      { text: "Еженедельные выводы о питании." },
      { text: "Совместное семейное планирование (планируемая функция)." },
    ] },
    { type: "p", text: "Функции могут изменяться, удаляться или быть временно недоступными." },

    // 5. Subscription and Payment Terms
    { type: "h2", text: "5. Условия подписки и оплаты" },
    { type: "h3", text: "5.1 Тарифы подписки" },
    { type: "p", text: "MealPill предлагает платные подписки. Текущие тарифы включают:" },
    { type: "ul", items: [
      { text: "Индивидуальную подписку (доступна сейчас)." },
      { text: "Семейную подписку (скоро)." },
    ] },
    { type: "p", text: "Стоимость и период оплаты (месячный или годовой) отображаются до оплаты. Подписки продлеваются автоматически в конце каждого расчётного периода, если они не отменены." },
    { type: "h3", text: "5.2 Бесплатный пробный период" },
    { type: "p", text: "Новые подписки включают 7-дневный бесплатный пробный период. Для начала пробного периода требуется действительный способ оплаты. Если вы не отмените подписку до окончания пробного периода, с вашего способа оплаты будет автоматически списана стоимость выбранного плана в день окончания пробного периода по цене, указанной при оформлении. Вы можете отменить подписку в любое время в течение пробного периода через портал управления подпиской без взимания платы." },
    { type: "h3", text: "5.3 Платежи" },
    { type: "p", text: "Платежи обрабатывает Stripe, Inc. Предоставляя платёжные данные, вы уполномочиваете MealPill списывать средства с выбранного вами способа оплаты." },
    { type: "h3", text: "5.4 Возврат средств" },
    { type: "p", text: "На первую платную подписку мы предоставляем 14-дневную гарантию удовлетворённости. По истечении 14 дней средства не возвращаются, кроме случаев, предусмотренных законом. Вы можете отменить подписку в любое время, чтобы прекратить дальнейшие списания. Настоящий пункт не ограничивает возмещение, на которое вы имеете право согласно Разделу 5.5 или согласно применимому законодательству о защите прав потребителей." },
    { type: "h3", text: "5.5 Автоматическое продление и отмена" },
    { type: "h3", text: "5.5.1 Месячные планы" },
    { type: "p", text: "Ваша месячная подписка продлевается автоматически в конце каждого платёжного периода. Вы можете отменить продление в любое время — как до, так и после даты продления — через портал управления подпиской, без взимания платы или штрафа." },
    { type: "h3", text: "5.5.2 Годовые планы" },
    { type: "p", text: "Ваша годовая подписка продлевается автоматически в конце каждого 12-месячного срока. Не менее чем за 30 дней и не более чем за 60 дней до каждой даты продления мы отправим уведомление на адрес электронной почты вашего аккаунта с указанием даты продления, продолжительности нового срока, суммы к списанию и способа отмены. Если вы отмените подписку до даты продления, плата или штраф не взимаются. Если вы отмените подписку после даты продления, вы имеете право на возмещение части платы за продление, соответствующей неиспользованной части нового срока; мы осуществим такое возмещение в течение 15 дней с момента получения вашего уведомления об отмене." },

    // 6. AI-Generated Content Disclaimer
    { type: "h2", text: "6. Оговорка о контенте, сгенерированном ИИ" },
    { type: "p", text: "Этот раздел критически важен. Прочитайте внимательно.", className: "font-medium uppercase tracking-wide text-[#9A6B5C] text-sm mb-3" },
    { type: "p", text: "MealPill использует ИИ для создания планов питания, рецептов, данных о пищевой ценности, замен ингредиентов и диетических рекомендаций. Результат работы ИИ:" },
    { type: "ul", items: [
      { html: "<strong>Не является медицинской, нутрициологической или диетической рекомендацией.</strong> За персональными рекомендациями обратитесь к лицензированному диетологу, нутрициологу или врачу." },
      { html: "<strong>Может содержать ошибки, неточности или устаревшую информацию.</strong> Проверяйте данные об аллергенах и диетических ограничениях перед употреблением." },
      { html: "<strong>Может неточно учитывать ваше состояние здоровья, аллергии или диетические требования.</strong>" },
    ] },
    { type: "h3", text: "6.1 Предупреждение об аллергенах и диетических ограничениях" },
    { type: "p", text: "Если у вас есть пищевая аллергия, диетические ограничения или особые состояния здоровья:" },
    { type: "ul", items: [
      { text: "Вы сами отвечаете за проверку каждого ингредиента и рецепта перед употреблением." },
      { text: "Не полагайтесь исключительно на фильтры или предложения MealPill." },
      { text: "Сверяйте информацию с этикетками продуктов и надёжными медицинскими источниками." },
      { text: "MealPill не несёт ответственности за побочные реакции, заболевания или вред, вызванные доверием к контенту, сгенерированному ИИ." },
    ] },
    { type: "h3", text: "6.2 Точность рецептов" },
    { type: "p", text: "Рецепты могут содержать ошибки в мерах, пропущенные шаги или неверное время приготовления. Полагайтесь на собственный кулинарный опыт." },

    // 7. User Content
    { type: "h2", text: "7. Контент пользователя" },
    { type: "h3", text: "7.1 Ваш контент" },
    { type: "p", text: "Вы можете создавать или загружать контент (собственные рецепты, заметки, фотографии). Вы сохраняете право собственности на него, но предоставляете MealPill всемирную, неисключительную, безвозмездную лицензию на:" },
    { type: "ul", items: [
      { text: "Хранение, отображение и обработку вашего контента для предоставления Сервиса." },
      { text: "Улучшение функций продукта (с использованием агрегированных обезличенных данных)." },
    ] },
    { type: "h3", text: "7.2 Правила в отношении контента" },
    { type: "p", text: "Вы не вправе загружать контент, который:" },
    { type: "ul", items: [
      { text: "Нарушает права интеллектуальной собственности других лиц." },
      { text: "Является незаконным, вредоносным, оскорбительным или клеветническим." },
      { text: "Содержит вредоносное программное обеспечение или эксплойты." },
      { text: "Пропагандирует вредное пищевое поведение или небезопасные диетические практики." },
      { text: "Нарушает применимое законодательство." },
    ] },
    { type: "p", text: "Мы можем удалить контент, нарушающий эти правила, без предупреждения." },

    // 8. Acceptable Use
    { type: "h2", text: "8. Допустимое использование" },
    { type: "p", text: "Вы обязуетесь не:" },
    { type: "ul", items: [
      { text: "Осуществлять обратную разработку, декомпиляцию или попытки извлечь исходный код." },
      { text: "Использовать Сервис для создания конкурирующего продукта." },
      { text: "Осуществлять скрейпинг или массовый сбор данных из Сервиса." },
      { text: "Пытаться обойти ограничения частоты запросов, средства безопасности или платный доступ." },
      { text: "Выдавать себя за других лиц." },
      { text: "Использовать автоматизированные системы (боты) для доступа к Сервису без разрешения." },
      { text: "Перепродавать, сублицензировать или коммерчески эксплуатировать Сервис." },
    ] },

    // 9. Intellectual Property
    { type: "h2", text: "9. Интеллектуальная собственность" },
    { type: "p", text: "Название MealPill, логотип, программное обеспечение, дизайн и оригинальный контент принадлежат Дмитрию Кравчуку и защищены законодательством об авторском праве, товарных знаках и иными нормами. Заявка на регистрацию товарного знака MealPill подана и находится на рассмотрении." },
    { type: "p", text: "Вы не вправе использовать наши товарные знаки без предварительного письменного разрешения." },

    // 10. Third-Party Services
    { type: "h2", text: "10. Сервисы третьих сторон" },
    { type: "p", text: "Сервис интегрируется с:" },
    { type: "ul", items: [
      { text: "Google Gemini (обработка ИИ)." },
      { text: "Stripe (платежи)." },
      { text: "Railway, Vercel (хостинг)." },
    ] },
    { type: "p", text: "Использование вами сервисов третьих сторон регулируется их собственными условиями и политиками конфиденциальности." },

    // 11. Disclaimers
    { type: "h2", text: "11. Оговорки об отсутствии гарантий" },
    { type: "p", text: "Сервис предоставляется «как есть» и «как доступно», без каких-либо гарантий, прямых или подразумеваемых, включая, в частности:", className: "uppercase tracking-wide text-sm font-medium" },
    { type: "ul", items: [
      { text: "Гарантии пригодности для продажи, пригодности для определённой цели, отсутствия нарушения прав третьих лиц." },
      { text: "Гарантии точности, надёжности или полноты контента." },
      { text: "Гарантии бесперебойной, защищённой или безошибочной работы Сервиса." },
      { text: "Гарантии точности данных о пищевой ценности или пригодности для любого состояния здоровья." },
    ] },
    { type: "p", text: "Мы не гарантируем конкретных результатов от использования Сервиса (например, снижения веса, экономии на продуктах, экономии времени)." },

    // 12. Limitation of Liability
    { type: "h2", text: "12. Ограничение ответственности" },
    { type: "p", text: "В максимальном объёме, допустимом законом:", className: "uppercase tracking-wide text-sm font-medium" },
    { type: "ul", items: [
      { text: "MealPill (Дмитрий Кравчук) не несёт ответственности за косвенные, случайные, специальные, побочные или штрафные убытки." },
      { text: "Совокупная ответственность по любому требованию не превышает суммы, уплаченной вами MealPill в течение 12 месяцев до предъявления требования, или 100 канадских долларов — в зависимости от того, что больше." },
      { text: "MealPill не несёт ответственности за убытки, вызванные: контентом, сгенерированным ИИ; аллергическими реакциями; пищевым отравлением; вредом от диеты; сервисами третьих сторон; обстоятельствами непреодолимой силы." },
    ] },
    { type: "p", text: "Некоторые юрисдикции не допускают исключения определённых гарантий или ограничения ответственности — эти положения применяются в максимальном объёме, допустимом законом." },

    // 13. Indemnification
    { type: "h2", text: "13. Возмещение убытков" },
    { type: "p", text: "Вы обязуетесь возместить и оградить Дмитрия Кравчука и MealPill от любых претензий, убытков или расходов, возникающих вследствие:" },
    { type: "ul", items: [
      { text: "Вашего использования Сервиса." },
      { text: "Вашего пользовательского контента." },
      { text: "Нарушения вами настоящих Условий." },
      { text: "Нарушения вами любых прав третьих лиц." },
    ] },

    // 14. Termination
    { type: "h2", text: "14. Прекращение" },
    { type: "h3", text: "14.1 По вашей инициативе" },
    { type: "p", text: "Вы можете прекратить действие своего аккаунта в любое время, написав на hello@mealpill.app или воспользовавшись функциями удаления аккаунта в настройках." },
    { type: "h3", text: "14.2 По нашей инициативе" },
    { type: "p", text: "Мы можем приостановить или прекратить действие вашего аккаунта, если вы:" },
    { type: "ul", items: [
      { text: "Нарушаете настоящие Условия." },
      { text: "Совершаете мошеннические или вредоносные действия." },
      { text: "Не оплачиваете стоимость подписки." },
      { text: "Неактивны более 24 месяцев." },
    ] },
    { type: "p", text: "После прекращения ваш доступ завершается, но разделы 6, 8, 9, 11, 12, 13, 15 и 16 продолжают действовать." },

    // 15. Governing Law and Disputes
    { type: "h2", text: "15. Применимое право и споры" },
    { type: "p", text: "Настоящие Условия регулируются законодательством провинции Британская Колумбия, Канада, без учёта коллизионных норм." },
    { type: "h3", text: "15.1 Неформальное урегулирование" },
    { type: "p", text: "В случае любого спора сначала обратитесь к нам на hello@mealpill.app. Мы будем стремиться урегулировать спор неформально в течение 30 дней." },
    { type: "h3", text: "15.2 Подсудность" },
    { type: "p", text: "Если неформальное урегулирование не удастся, споры будут рассматриваться судами провинции Британская Колумбия, Канада. Вы отказываетесь от любых возражений относительно подсудности или места рассмотрения." },

    // 16. Changes to Terms
    { type: "h2", text: "16. Изменения Условий" },
    { type: "h3", text: "16.1 Положения, которые могут быть изменены" },
    { type: "p", text: "Мы можем в одностороннем порядке изменять следующие положения настоящих Условий: Раздел 4 (Описание Сервиса), Раздел 8 (Приемлемое использование), Раздел 10 (Сторонние сервисы) и стоимость подписки согласно Разделу 5.1. Никакое другое положение не может быть изменено без вашего согласия." },
    { type: "h3", text: "16.2 Ограничения в отношении изменений" },
    { type: "p", text: "Мы не будем изменять положения, касающиеся отмены, возврата, обмена или возмещения, таким образом, который увеличивает ваши обязанности или уменьшает наши." },
    { type: "h3", text: "16.3 Уведомление об изменениях" },
    { type: "p", text: "Мы уведомим вас о любом изменении согласно Разделу 16.1 не менее чем за 30 дней и не более чем за 60 дней до вступления его в силу, письмом на адрес электронной почты вашего аккаунта. В уведомлении будут указаны действующее положение, изменённое положение, дата вступления изменения в силу, а также ваше право отменить подписку без взимания платы или штрафа, если вы не принимаете изменение." },

    // 17. General
    { type: "h2", text: "17. Общие положения" },
    { type: "ul", items: [
      { html: "<strong>Полнота соглашения:</strong> Настоящие Условия (вместе с Политикой конфиденциальности) составляют полное соглашение между сторонами." },
      { html: "<strong>Делимость:</strong> Если какое-либо положение окажется неисполнимым, остальные сохраняют силу." },
      { html: "<strong>Отсутствие отказа от прав:</strong> Неприменение нами какого-либо положения не означает отказа от него." },
      { html: "<strong>Передача прав:</strong> Вы не можете передавать настоящие Условия; мы можем передать их в связи с переходом бизнеса." },
      { html: "<strong>Язык документа:</strong> Настоящие Условия составлены на английском языке. Переводы предоставлены исключительно для удобства. В случае любых расхождений между английской версией и переводом преимущественную силу имеет английская версия." },
    ] },

    // 18. Contact
    { type: "h2", text: "18. Контакты" },
    { type: "p", text: "Вопросы по настоящим Условиям:" },
    { type: "p", html: "<strong>Email:</strong> hello@mealpill.app<br /><strong>Адрес:</strong> Дмитрий Кравчук, 525-503 Ninth Street, New Westminster, BC V3M 5T9, Canada" },
  ],
};
